import { showButtonLoading } from "../ui/button-loading";
import type { CustomValidationService } from "../validation/validation-service";

// The event booking page's Select Tickets and Delegate Information step
// (Views/Components/EventBookingForm/Default.cshtml). A booking can mix ticket packages (50464):
// each package's quantity adds or removes that package's delegate panels, grouped in the table's
// order, and keeps whatever was already typed into the panels that stay. Each delegate's email is
// checked with the server as it's entered (already booked, eligible to attend), and Checkout is
// disabled while any of them is rejected.
//
// Validation is jQuery Validate's, as on Register: the delegate fields carry the model's data-val
// rules (Partials/EventBookingDelegateFields.cshtml) and show their messages beneath them. The form
// is re-parsed whenever panels are added or renumbered, and fields in closed panels are validated
// too, with their panels opened when a submit fails.

declare const $: any; // jQuery and jQuery Validate, loaded by script tags in Layout.cshtml

const EMAIL_CHECK_URL = "/umbraco/surface/eventbookingsurface/validatemembership";
const NO_TICKETS_MESSAGE = "Please choose at least one ticket.";

// Panels added here get ids of their own, apart from the server's delegate-panel-s{n}.
let panelCount = 0;

export function initBookingPage(validationService?: CustomValidationService): void {
    const form = document.getElementById("EventBookingPageForm") as HTMLFormElement | null;
    const list = form?.querySelector<HTMLElement>("#event-delegates");
    const section = form?.querySelector<HTMLElement>("#event-delegates-section");
    const template = document.getElementById("event-delegate-template") as HTMLTemplateElement | null;
    const ticketsError = form?.querySelector<HTMLElement>("#event-booking-tickets-error");

    if (!form || !list || !section || !template || !ticketsError) {
        return;
    }

    // aspnet-client-validation picks up data-val forms when the page loads, but can't see panels
    // added later and skips hidden fields, so this form is left to jQuery Validate alone.
    validationService?.release(form);
    parseValidation(form);

    const quantities = Array.from(form.querySelectorAll<HTMLSelectElement>("select.event-quantity"));
    const quantityOf = (select: HTMLSelectElement) => parseInt(select.value, 10) || 0;
    const totalQuantity = () => quantities.reduce((total, select) => total + quantityOf(select), 0);

    const showTicketsError = (message: string | null) => {
        ticketsError.textContent = message ?? "";
        ticketsError.hidden = message === null;
    };

    quantities.forEach(select => select.addEventListener("change", () => {
        syncDelegates(form, list, section, template, quantities);

        if (totalQuantity() > 0) {
            showTicketsError(null);
        }
    }));

    list.addEventListener("focusout", e => {
        const target = e.target as HTMLElement;
        if (target.matches(".input-del-email")) {
            void checkEmail(target as HTMLInputElement, list.dataset.eventId ?? "");
        }
    });

    // A changed email needs checking again.
    list.addEventListener("input", e => {
        const target = e.target as HTMLElement;
        if (target.matches(".input-del-email")) {
            setEmailState(target as HTMLInputElement, null);
        }
    });

    // jQuery Validate can't focus a field in a closed panel, so open every panel with an error.
    $(form).on("invalid-form.validate", (_e: unknown, validator: any) => {
        validator.errorList.forEach((error: { element: HTMLElement }) => openPanelOf(error.element));
    });

    form.addEventListener("submit", e => {
        if (totalQuantity() > 0) {
            return;
        }

        // Stop here, before jQuery Validate or form-service lock the buttons.
        e.preventDefault();
        e.stopImmediatePropagation();
        showTicketsError(NO_TICKETS_MESSAGE);
        quantities[0]?.focus();
    }, true);

    // "Processing..." once the form is on its way. Listened for on the document, after every
    // handler on the form itself: jQuery Validate binds there only once the first delegate panel
    // exists, so later than this, and cancels the submit when a field is wrong.
    document.addEventListener("submit", e => {
        if (e.target === form && !e.defaultPrevented) {
            showButtonLoading(form.querySelector<HTMLButtonElement>("#event-booking-submit"));
        }
    });

    // Delegates restored from session: show the result of their earlier email check, and treat
    // an email the server rejected on the last post as rejected, until it's changed.
    list.querySelectorAll<HTMLInputElement>(".input-del-email").forEach(input => {
        const item = input.closest(".delegate-form");
        const validated = item?.querySelector<HTMLInputElement>("input[name$='.Validated']");
        const serverError = item?.querySelector(`.field-validation-error[data-valmsg-for='${input.name}']`)?.textContent?.trim();

        if (input.value && serverError) {
            input.dataset.emailState = "invalid";
            input.dataset.emailMessage = serverError;
            input.classList.add("is-invalid");
        } else if (input.value && validated?.value.toLowerCase() === "true") {
            setEmailState(input, true);
        }
    });

    updateSubmit(list);
}

/**
 * Gives jQuery Validate the form's current fields. Its rules are cached against the form, so
 * they're cleared first or new and renumbered fields would be missed (as in form-service.ts).
 */
function parseValidation(form: HTMLFormElement): void {
    if (!$?.validator?.unobtrusive) {
        return;
    }

    $(form).removeData("validator").removeData("unobtrusiveValidation");
    $.validator.unobtrusive.parse(form);

    // Validate the fields in closed panels too; jQuery Validate skips hidden fields by default.
    const validator = $(form).data("validator");
    if (!validator) {
        return;
    }

    validator.settings.ignore = "";

    // A rejected email is a rule like the others, so the message survives any later validation
    // of the field until the email changes.
    if (!$.validator.methods.lmaEmailAccepted) {
        $.validator.addMethod(
            "lmaEmailAccepted",
            (_value: string, element: HTMLInputElement) => element.dataset.emailState !== "invalid",
            (_params: unknown, element: HTMLInputElement) => element.dataset.emailMessage ?? "This email address can't be booked.");
    }

    // One person takes one place: an email an earlier delegate has is an error on the later one,
    // as on the server (EventBookingHelper.DuplicateEmailIndexes).
    if (!$.validator.methods.lmaEmailUnique) {
        $.validator.addMethod(
            "lmaEmailUnique",
            (value: string, element: HTMLInputElement) => !isDuplicateEmail(element, value),
            (_params: unknown, element: HTMLInputElement) => `${element.value.trim()} is already a delegate on this booking.`);
    }

    form.querySelectorAll<HTMLInputElement>(".input-del-email").forEach(input => $(input).rules("add", { lmaEmailAccepted: true, lmaEmailUnique: true }));
}

/** Whether a delegate before this one in the list already has the email (ignoring case and spaces). */
function isDuplicateEmail(input: HTMLInputElement, value: string): boolean {
    const email = value.trim().toLowerCase();
    if (!email) {
        return false;
    }

    const emails = Array.from(input.form?.querySelectorAll<HTMLInputElement>("#event-delegates .input-del-email") ?? []);
    return emails
        .slice(0, emails.indexOf(input))
        .some(other => other.value.trim().toLowerCase() === email);
}

/** Adds, keeps or removes delegate panels to match the quantities, then renumbers them. */
function syncDelegates(form: HTMLFormElement, list: HTMLElement, section: HTMLElement, template: HTMLTemplateElement, quantities: HTMLSelectElement[]): void {
    const current = Array.from(list.querySelectorAll<HTMLElement>(":scope > .delegate-form"));
    const byPackage = new Map<string, HTMLElement[]>();

    current.forEach(item => {
        const packageId = item.dataset.packageId ?? "";
        byPackage.set(packageId, [...(byPackage.get(packageId) ?? []), item]);
    });

    const wanted: HTMLElement[] = [];

    quantities.forEach(select => {
        const packageId = select.dataset.packageId ?? "";
        const kept = byPackage.get(packageId) ?? [];
        const quantity = parseInt(select.value, 10) || 0;

        for (let i = 0; i < quantity; i++) {
            wanted.push(kept[i] ?? createDelegate(template, packageId, select.dataset.packageName ?? ""));
        }
    });

    current.filter(item => !wanted.includes(item)).forEach(item => item.remove());

    // Appending an item that's already in the list moves it, which puts them in order.
    wanted.forEach((item, index) => {
        list.appendChild(item);
        renumber(item, index);
    });

    section.hidden = wanted.length === 0;

    // Keep one panel open so there's always somewhere to start typing.
    if (wanted.length > 0 && !list.querySelector(".accordion-collapse.show")) {
        setPanelOpen(wanted[0], true);
    }

    parseValidation(form);

    // Removing a rejected delegate's panel can free up Checkout.
    updateSubmit(list);
}

function createDelegate(template: HTMLTemplateElement, packageId: string, packageName: string): HTMLElement {
    const html = template.innerHTML
        .replace(/\{\{package_id\}\}/g, packageId)
        .replace(/\{\{panel_id\}\}/g, `delegate-panel-n${++panelCount}`);

    const wrapper = document.createElement("div");
    wrapper.innerHTML = html.trim();
    const item = wrapper.firstElementChild as HTMLElement;

    // Set as text, as the package name comes from D365.
    const name = item.querySelector(".delegate-form__package");
    if (name) {
        name.textContent = packageName;
    }

    return item;
}

/** Points the panel's fields and messages at Delegates[index] and shows its number. */
function renumber(item: HTMLElement, index: number): void {
    item.querySelectorAll<HTMLElement>("[name], [id], [for], [data-valmsg-for]").forEach(node => {
        ["name", "id", "for", "data-valmsg-for"].forEach(attr => {
            const value = node.getAttribute(attr);
            if (value) {
                node.setAttribute(attr, value
                    .replace(/Delegates\[\d+\]/g, `Delegates[${index}]`)
                    .replace(/Delegates_\d+__/g, `Delegates_${index}__`));
            }
        });
    });

    const number = item.querySelector(".delegate-form__number");
    if (number) {
        number.textContent = (index + 1).toString();
    }
}

function openPanelOf(element: HTMLElement): void {
    const item = element.closest<HTMLElement>(".delegate-form");
    if (item) {
        setPanelOpen(item, true);
    }
}

// Toggled directly rather than through Bootstrap's Collapse, so it also works before Bootstrap
// has set up a panel the script has just added.
function setPanelOpen(item: HTMLElement, open: boolean): void {
    item.querySelector(".accordion-collapse")?.classList.toggle("show", open);
    const button = item.querySelector(".accordion-button");
    button?.classList.toggle("collapsed", !open);
    button?.setAttribute("aria-expanded", open ? "true" : "false");
}

async function checkEmail(input: HTMLInputElement, eventId: string): Promise<void> {
    const email = input.value.trim();

    // An empty or malformed email is jQuery Validate's to report.
    if (!email || !input.checkValidity()) {
        setEmailState(input, null);
        return;
    }

    // A spinner in the field and "Checking…" under it while D365 is asked. Checkout isn't
    // disabled meanwhile: leaving the field for Checkout starts this check, and a button
    // disabled under the pointer would swallow that click. The server checks again on submit.
    input.classList.add("input-loading");
    showEmailChecking(input, email);

    try {
        // The delegate's own index, from its field's name (Delegates[2].EmailAddress).
        const delIndex = /Delegates\[(\d+)\]/.exec(input.name)?.[1] ?? "0";
        const query = new URLSearchParams({ email, delIndex, eventId });
        const response = await fetch(`${EMAIL_CHECK_URL}?${query}`, { headers: { Accept: "application/json" } });
        const result = response.ok ? await response.json() as { valid?: boolean; reason?: string } : null;

        // Ignore a reply for an email that's since been changed.
        if (input.value.trim() !== email) {
            return;
        }

        // No answer, e.g. a 401 once the member's sign-in has lapsed, isn't a rejection: the
        // server checks again when the form is posted.
        setEmailState(input, result ? result.valid === true : null, result?.reason);
    } catch {
        // Leave it to the server's check when the form is posted.
        setEmailState(input, null);
    } finally {
        input.classList.remove("input-loading");
    }
}

function showEmailChecking(input: HTMLInputElement, email: string): void {
    const accepted = input.closest(".delegate-form")?.querySelector<HTMLElement>(".validated-member-txt");

    if (accepted) {
        accepted.classList.add("is-checking");
        accepted.style.display = "";
        accepted.textContent = `Checking ${email}…`;
    }
}

/** Checkout stays disabled while any delegate's email has been rejected. */
function updateSubmit(list: HTMLElement): void {
    const submit = list.closest("form")?.querySelector<HTMLButtonElement>("#event-booking-submit");
    if (submit) {
        submit.disabled = list.querySelector(".input-del-email[data-email-state='invalid']") !== null;
    }
}

/**
 * Shows an email check's result: true can attend, false can't, null not checked. A rejection is
 * shown as the field's validation message, like any other error on the form.
 */
function setEmailState(input: HTMLInputElement, valid: boolean | null, reason?: string): void {
    const item = input.closest(".delegate-form");
    const validated = item?.querySelector<HTMLInputElement>("input[name$='.Validated']");
    const accepted = item?.querySelector<HTMLElement>(".validated-member-txt");

    if (validated) {
        validated.value = valid === true ? "true" : "false";
    }

    const wasRejected = input.dataset.emailState === "invalid";

    if (valid === null) {
        delete input.dataset.emailState;
        delete input.dataset.emailMessage;
    } else {
        input.dataset.emailState = valid ? "valid" : "invalid";
        input.dataset.emailMessage = valid ? "" : `${input.value.trim()} ${reason || "could not be added."}`;
    }

    // Bootstrap's tick or red icon in the field marks the result of the check with D365, so the
    // icons appear only for that and not for an empty or malformed email.
    input.classList.toggle("is-valid", valid === true);
    input.classList.toggle("is-invalid", valid === false);

    // Show the rejection through the lmaEmailAccepted rule, or clear it once the email changes.
    if (valid === false || wasRejected) {
        $(input.form).data("validator")?.element(input);
    }

    if (accepted) {
        accepted.classList.remove("is-checking");
        accepted.style.display = valid === true ? "" : "none";
        accepted.textContent = valid === true ? `${input.value.trim()} can attend this event.` : "";
    }

    const list = input.closest<HTMLElement>("#event-delegates");
    if (list) {
        updateSubmit(list);
    }
}
